// ─── ScheduleForm ─────────────────────────────────────────────────────────────
// Shared timing UI for the four Report → Share/Export modals (Email, FTP,
// GLOS, NDBC). Renders the "Enable Schedule / Only Send New Data" pair,
// a 7-day checkbox grid, a Schedule Type dropdown, and up to 4 Send At
// time slots — matching the XD reference.
//
// Controlled via `value` + `onChange`. `value` shape (all keys optional):
//   {
//     enabled:       boolean,
//     only_new_data: boolean,
//     days_of_week:  "0101010"   // Sun..Sat "0"/"1" mask
//     schedule_type: "fixed"     // reserved for future variants
//     send_at:       ["09:00", "13:00", ...]  // up to 4 HH:MM slots
//   }

const DAYS = [
  { key: 0, label: 'Sunday'    },
  { key: 1, label: 'Monday'    },
  { key: 2, label: 'Tuesday'   },
  { key: 3, label: 'Wednesday' },
  { key: 4, label: 'Thursday'  },
  { key: 5, label: 'Friday'    },
  { key: 6, label: 'Saturday'  },
]

const MAX_SLOTS = 4

export default function ScheduleForm({ value, onChange }) {
  const v = normalize(value)

  const setKey = (k, val) => onChange({ ...v, [k]: val })

  const toggleDay = (idx) => {
    const chars = v.days_of_week.split('')
    chars[idx] = chars[idx] === '1' ? '0' : '1'
    setKey('days_of_week', chars.join(''))
  }

  const setSlot = (idx, val) => {
    const next = [...v.send_at]
    while (next.length < MAX_SLOTS) next.push('')
    next[idx] = val
    setKey('send_at', next)
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!v.enabled}
            onChange={(e) => setKey('enabled', e.target.checked)}
          />
          <span>Enable Schedule</span>
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!v.only_new_data}
            onChange={(e) => setKey('only_new_data', e.target.checked)}
          />
          <span>Only Send New Data</span>
        </label>
      </div>

      <div>
        <div className="text-xs font-semibold text-gray-700 mb-1">Days</div>
        <div className="grid grid-cols-3 gap-y-1 gap-x-4">
          {DAYS.map((d) => (
            <label key={d.key} className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={v.days_of_week[d.key] === '1'}
                onChange={() => toggleDay(d.key)}
              />
              <span>{d.label}</span>
            </label>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-semibold text-gray-700">Schedule Type</span>
        <select
          value={v.schedule_type}
          onChange={(e) => setKey('schedule_type', e.target.value)}
          className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-sm focus:outline-none focus:border-[#358ECD]"
        >
          <option value="fixed">Fixed Time</option>
        </select>
      </label>

      <div>
        <div className="text-xs font-semibold text-gray-700 mb-1">Send At</div>
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: MAX_SLOTS }).map((_, i) => (
            <input
              key={i}
              type="time"
              value={v.send_at[i] || ''}
              onChange={(e) => setSlot(i, e.target.value)}
              placeholder="Choose Time"
              className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]"
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// Fill in missing fields so the checked-attribute etc. never receives
// undefined and React doesn't switch controlled → uncontrolled between
// renders.
function normalize(v) {
  return {
    enabled:       v?.enabled       ?? true,
    only_new_data: v?.only_new_data ?? false,
    days_of_week:  (v?.days_of_week ?? '0000000').padEnd(7, '0').slice(0, 7),
    schedule_type: v?.schedule_type ?? 'fixed',
    send_at:       Array.isArray(v?.send_at) ? v.send_at.slice(0, MAX_SLOTS) : [],
  }
}

export const emptyScheduleValue = () => ({
  enabled:       true,
  only_new_data: false,
  days_of_week:  '0000000',
  schedule_type: 'fixed',
  send_at:       [],
})
